import Eyebrow from "../../Eyebrow";
import Reveal from "../../Reveal";
import TechNote from "../TechNote";
import { howItWorksSections } from "../../../../content/howItWorksSections";

/**
 * Small illustrative line chart: organizational memory retained over time,
 * one line climbing continuously, the other plateauing early. White-only —
 * the two series are told apart by opacity and dash style, not color.
 * Translation: Small illustrative line chart: organizational memory retained over time,
 * one line climbing continuously, the other plateauing early. White-only —
 * the two series are told apart by opacity and dash style, not color.
 *
 * @returns {JSX.Element} Memory-growth chart.
 */
const MemoryGrowthChart = () => (
  <svg viewBox="0 0 600 260" fill="none" xmlns="http://www.w3.org/2000/svg">
    {[70, 130, 190].map((y) => (
      <line key={y} x1="60" y1={y} x2="460" y2={y} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
    ))}

    <line x1="60" y1="20" x2="60" y2="220" stroke="rgba(255,255,255,0.25)" strokeWidth="1" />
    <line x1="60" y1="220" x2="460" y2="220" stroke="rgba(255,255,255,0.25)" strokeWidth="1" />

    <text x="60" y="238" fontSize="11" fontFamily="Public Sans, sans-serif" fontWeight="300" letterSpacing="0.08em" fill="rgba(255,255,255,0.45)">
      TIME →
    </text>
    <text x="60" y="14" fontSize="11" fontFamily="Public Sans, sans-serif" fontWeight="300" letterSpacing="0.08em" fill="rgba(255,255,255,0.45)">
      ORGANIZATIONAL MEMORY
    </text>

    <polyline
      points="60,205 160,165 260,128 360,88 460,50"
      fill="none"
      stroke="rgba(255,255,255,0.9)"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="460" cy="50" r="4.5" fill="rgba(255,255,255,0.9)" />
    <text x="472" y="46" fontSize="13" fontFamily="Public Sans, sans-serif" fontWeight="400" fill="rgba(255,255,255,0.92)">
      Kanzi
    </text>
    <text x="472" y="61" fontSize="10.5" fontFamily="Public Sans, sans-serif" fontWeight="300" fill="rgba(255,255,255,0.5)">
      keeps growing
    </text>

    <polyline
      points="60,208 140,160 220,150 460,148"
      fill="none"
      stroke="rgba(255,255,255,0.4)"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray="2 6"
    />
    <circle cx="460" cy="148" r="4.5" fill="rgba(255,255,255,0.4)" />
    <text x="472" y="144" fontSize="13" fontFamily="Public Sans, sans-serif" fontWeight="400" fill="rgba(255,255,255,0.55)">
      Generic Agents
    </text>
    <text x="472" y="159" fontSize="10.5" fontFamily="Public Sans, sans-serif" fontWeight="300" fill="rgba(255,255,255,0.4)">
      hits a hard cap
    </text>
  </svg>
);

/**
 * Four-stage watch/investigate/fix/verify timeline, reusing the homepage's
 * sticky-left / scrolling-right approach layout.
 * Translation: Four-stage watch/investigate/fix/verify timeline, reusing the homepage's
 * sticky-left / scrolling-right approach layout.
 *
 * @returns {JSX.Element} Approach section.
 */
const Approach = () => {
  const { approach, techNotes } = howItWorksSections;
  const steps = approach.steps;

  return (
    <section id="how-it-works" className="section-block section-block--framed kanzi-approach-section">
      <div className="kanzi-approach__split">
        <div className="kanzi-approach__left">
          <div className="kanzi-approach__left-inner">
            <Eyebrow>
              {approach.eyebrowLabel}
              <TechNote>{techNotes[approach.techNote]}</TechNote>
            </Eyebrow>
            <h2 className="kanzi-approach__title">{approach.title}</h2>
            <p className="kanzi-approach__desc">{approach.description}</p>
            {approach.detail && <p className="hiw-detail-text">{approach.detail}</p>}
            <p className="kanzi-approach__footer">{approach.footer}</p>

            <div className="hiw-approach-chart" aria-hidden="true">
              <MemoryGrowthChart />
            </div>
            <p className="hiw-detail-text">
              Illustrative — organizational memory retained over time, not a literal measurement.
            </p>
          </div>
        </div>

        <div className="kanzi-approach__right">
          {steps.map((step, index) => (
            <div key={step.number} className="kanzi-timeline-zone">
              <Reveal variant="fade-up">
                <div className="kanzi-timeline-step">
                  <div className="kanzi-timeline-step__track">
                    <div className="kanzi-timeline-step__circle">
                      <span>{step.number}</span>
                    </div>
                    {index < steps.length - 1 && <div className="kanzi-timeline-step__connector" />}
                  </div>
                  <div className="kanzi-timeline-step__content">
                    <h3 className="kanzi-timeline-step__title">{step.title}</h3>
                    <p className="kanzi-timeline-step__desc">{step.description}</p>
                    {step.detail && <p className="hiw-detail-text">{step.detail}</p>}
                  </div>
                </div>
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Approach;
